import { useCallback } from 'react';
import { type GroupProps, Group, Text, Modal, LoadingOverlay, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { SyncConfigDropdown } from '../DropdownMenus';
import { ConfirmationModal } from '../Modals';
import { defaultModalProps } from '../../utils/mantineUtils';
import type { StorageProviderSyncConfig, WithoutPermissions } from '../../types/apiTypes';
import useEnsisMutation from '../../hooks/useEnsisMutation';

type Props = {
  syncConfig: WithoutPermissions<StorageProviderSyncConfig>
} & GroupProps;

const SyncConfigItem: React.FC<Props> = ({ syncConfig, ...groupProps }: Props) => {
  const [opened, { open, close }] = useDisclosure();

  const deleteSyncConfigMutation = useEnsisMutation(
    `/app/storage-provider-sync-configs/${syncConfig?.uid}/data`,
    {
      requestType: 'delete',
      onSuccess: close,
      successMessage: 'Location removed',
      queryKeysToInvalidate: ['/app/storage-providers']
    }
  );

  const handleDelete = useCallback(() => {
    deleteSyncConfigMutation.mutate({});
  }, [deleteSyncConfigMutation.mutate]);

  return (
    <>
      <Modal opened={opened} {...defaultModalProps}>
        <LoadingOverlay visible={deleteSyncConfigMutation.isPending} />
        <ConfirmationModal
          titleText='Remove synced location?'
          bodyText={'Removing this location from Ensis will delete all Resources that were synced from it.' +
            'This will not affect the original documents in SharePoint. \n\nAre you sure you want to proceed?'}
          onCancel={close}
          onConfirm={handleDelete}
          confirmButtonText='Remove Location'
        />
      </Modal>
      <Group align='center' {...groupProps}>
        <SyncConfigDropdown onClickDelete={open} disabled={syncConfig.is_syncing} />
        <Stack maw={400}>
          <Stack gap={2}>
            <Text fz='sm' fw={500}>Site URL: </Text>
            <Text fz='sm'>
              {syncConfig?.sync_config?.site_url}
            </Text>
          </Stack>
          <Stack gap={2}>
            <Text fz='sm' fw={500}>Document Library: </Text>
            <Text fz='sm'>
              {syncConfig?.sync_config?.document_library_display_name}
            </Text>
          </Stack>
          <Stack gap={2}>
            <Text fz='sm' fw={500}>Folder Path: </Text>
            <Text fz='sm'>
              {`/${syncConfig?.sync_config?.starting_folder_path ?? ''}`}
            </Text>
          </Stack>
        </Stack>
      </Group>
    </>
  );
};

export default SyncConfigItem;
